import {
  borderRadius,
  colorSets,
  css,
  IconVisibilityOn,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
} from '@lessonup/ui-components';
import React from 'react';
import { usePlayerTranslation } from '../../hooks/usePlayerTranslation';

export interface MovableAnswerCardContentProps {
  answerText: string;
  revealed: boolean;
  isSelected: boolean;
  authorSlot?: React.ReactNode;
  handleReveal?: (e: React.MouseEvent) => void;
}

export const MovableAnswerCardContent: React.FC<MovableAnswerCardContentProps> = ({
  answerText,
  revealed,
  isSelected,
  authorSlot,
  handleReveal,
}) => {
  const { t } = usePlayerTranslation();

  const canToggle = !revealed || isSelected;

  return (
    <StyledContent direction="y" spacing={spacing.size8} padding={spacing.size4}>
      <StyledAnswerWrapper
        aria-label={revealed ? t('labels.hideAnswer') : t('labels.revealAnswer')}
        revealed={revealed}
        canToggle={canToggle}
        onClick={canToggle ? handleReveal : undefined}
        role="button"
        tabIndex={0}
      >
        {!revealed && <StyledIconEye width={16} height={16} />}
        <StyledAnswerText textStyle="caption" size="small" revealed={revealed}>
          {answerText}
        </StyledAnswerText>
      </StyledAnswerWrapper>
      {authorSlot && (
        <SpaceBetween direction="x" spacing={spacing.size4}>
          {authorSlot}
        </SpaceBetween>
      )}
    </StyledContent>
  );
};

const StyledContent = styled(SpaceBetween)`
  word-break: break-word;
`;

const StyledAnswerWrapper = styled.div<{ revealed: boolean; canToggle: boolean }>`
  cursor: ${({ canToggle }) => (canToggle ? 'pointer' : 'default')};
  text-align: left;
  border-radius: ${borderRadius.rounded2};
  padding: ${spacing.size0} ${spacing.size2};
  ${({ revealed, canToggle }) =>
    !revealed
      ? css`
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${colorSets.active.fill};
          color: ${colorSets.active.on};
          &:hover {
            background: ${colorSets.active.fillFeedback};
          }
        `
      : css`
          ${canToggle &&
          css`
            &:hover {
              background: ${colorSets.neutral.fillFeedback};
            }
          `}
        `}
`;

const StyledIconEye = styled(IconVisibilityOn)`
  position: absolute;
  user-select: none;
`;

const StyledAnswerText = styled(TextElement)<{ revealed: boolean }>`
  opacity: ${({ revealed }) => (revealed ? 1 : 0)};
  user-select: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;
